import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Delete,
  Notifications,
  Visibility,
  Person,
  DirectionsCar,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import config from '../config/config';

const NotificationHistoryPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getNotificationHistory({
        page: page + 1,
        limit: pageSize,
        userType: userTypeFilter,
        search: search,
      });

      if (response.success) {
        setNotifications(response.data.notifications);
        setTotalRows(response.data.pagination.total);
      } else {
        setError(response.message || 'Failed to load notifications');
      }
    } catch (err) {
      setError('Failed to load notification history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, userTypeFilter, search]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleSearch = () => {
    setPage(0);
    fetchNotifications();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const response = await apiService.deleteNotification(id);
      if (response.success) {
        fetchNotifications();
      } else {
        alert(response.message || 'Failed to delete notification');
      }
    } catch (err) {
      alert('Failed to delete notification');
    }
  };

  const handleView = (notification) => {
    setSelectedNotification(notification);
    setViewDialogOpen(true);
  };

  const columns = [
    {
      field: 'recipient',
      headerName: 'Recipient',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {params.row.user_type === 'driver' ? (
            <DirectionsCar color="primary" fontSize="small" />
          ) : (
            <Person color="secondary" fontSize="small" />
          )}
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.recipient_name || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.recipient_phone || '-'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'user_type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.user_type === 'driver' ? 'Driver' : 'Passenger'}
          color={params.row.user_type === 'driver' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 200,
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.message}>
          {params.row.message}
        </Typography>
      ),
    },
    {
      field: 'notification_type',
      headerName: 'Category',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.notification_type || 'general'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'is_read',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_read ? 'Read' : 'Unread'}
          color={params.row.is_read ? 'default' : 'info'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Sent At',
      width: 180,
      renderCell: (params) =>
        params.row.created_at
          ? new Date(params.row.created_at).toLocaleString()
          : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleView(params.row)}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Notification History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all sent notifications
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by title or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                label="User Type"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="passenger">Passengers</MenuItem>
                <MenuItem value="driver">Drivers</MenuItem>
              </Select>
            </FormControl>

            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <DataGrid
            rows={notifications}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 50]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            rowCount={totalRows}
            paginationMode="server"
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* View Notification Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications color="primary" />
            Notification Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedNotification && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Recipient
                </Typography>
                <Typography variant="body1">
                  {selectedNotification.recipient_name || 'Unknown'} ({selectedNotification.recipient_phone || '-'})
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  User Type
                </Typography>
                <Box>
                  <Chip
                    label={selectedNotification.user_type === 'driver' ? 'Driver' : 'Passenger'}
                    color={selectedNotification.user_type === 'driver' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedNotification.title}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Message
                </Typography>
                <Typography variant="body1">
                  {selectedNotification.message}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Category
                </Typography>
                <Box>
                  <Chip
                    label={selectedNotification.notification_type || 'general'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box>
                  <Chip
                    label={selectedNotification.is_read ? 'Read' : 'Unread'}
                    color={selectedNotification.is_read ? 'default' : 'info'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Sent At
                </Typography>
                <Typography variant="body1">
                  {selectedNotification.created_at
                    ? new Date(selectedNotification.created_at).toLocaleString()
                    : '-'}
                </Typography>
              </Box>

              {/* Display notification image if available */}
              {(() => {
                // Parse data field to get imageUrl
                let imageUrl = null;
                try {
                  if (selectedNotification.data) {
                    const dataObj = typeof selectedNotification.data === 'string'
                      ? JSON.parse(selectedNotification.data)
                      : selectedNotification.data;
                    if (dataObj && dataObj.imageUrl) {
                      // Handle image URL path
                      const imagePath = dataObj.imageUrl;
                      if (imagePath.startsWith('http')) {
                        imageUrl = imagePath;
                      } else if (imagePath.startsWith('uploads/')) {
                        // Remove 'uploads/' prefix since UPLOADS_BASE_URL already has it
                        imageUrl = `${config.UPLOADS_BASE_URL}/${imagePath.substring(8)}`;
                      } else {
                        imageUrl = `${config.UPLOADS_BASE_URL}/${imagePath}`;
                      }
                    }
                  }
                } catch (e) {
                  console.error('Error parsing notification data:', e);
                }

                if (imageUrl) {
                  return (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Image
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 2,
                          textAlign: 'center',
                        }}
                      >
                        <Box
                          component="img"
                          src={imageUrl}
                          alt="Notification"
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 300,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                          }}
                          onError={(e) => {
                            console.error('Image failed to load:', imageUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    </Box>
                  );
                }
                return null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationHistoryPage;
